import { useEffect, useMemo, useState, useRef } from 'react';
import { useQuery } from '@redux-requests/react';
import { fetchOrgIntegrations } from 'store/myAccount/requests';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDifferenceStatusFilters,
  getDifferenceTypeFilters,
  getDifferences,
  getFocusedDifferenceId,
  getInspectionId,
  getInspectionLastJobId,
  getInspectionSettings,
  getInternalAnnotationVisibility,
  getIsSingleFile,
  getShowTextHighlights,
  getTargetFile,
  report,
} from 'store';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Theme,
  Box,
} from '@mui/material';
import { abortRequests, stopPolling } from '@redux-requests/core';
import {
  createReport,
  CreateMultiInspectionReportPayload,
  CreateReportPayload,
  pollReportById,
  uploadReport,
} from 'store/report/requests';
import { makeStyles } from 'tss-react/mui';
import { GVTypography } from 'components/lib';
import GVCheckbox from 'components/lib/GVCheckbox/GVCheckbox';
import GVSelect from 'components/lib/GVSelect/GVSelect';
import { getStyleVariables, menuLightGrey } from 'styles/vars';
import { useUserStateStore } from 'zstore/userStateStore';
import { getOrganizationId, getTenantId } from 'utils/auth';
import { User } from '@auth0/auth0-react';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { useTracker } from 'components/Tracker/TrackerProvider';
import { ReportDestinations, ReportStates, REPORT_DOWNLOAD_DIALOG_Z_INDEX, useReportState } from 'zstore/reportStore';
import GVIconButton from 'components/lib/GVIconButton/GVIconButton';
import CloseIcon from '@mui/icons-material/Close';
import store from 'store/store';
import { CTFModuleTypes, DifferenceTypeNames, getDifferenceDisplayName, InspectionStatuses } from 'types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { colors } from 'components/lib/global/styles';
import { useOrganizationQuery } from 'store/queries/organizations/useOrganizationQuery';
import qs from 'qs';
import { useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { fetchInspection, fetchLinkedInspections } from 'store/request/inspections/actions';
import { ApiLinkedInspection } from 'store/request/inspections/types';
import { filterTypeMappings } from 'pdftron/docManager/hooks/useFilters';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styles from './ReportDownloadMenu.module.css';

export enum IntegrationNames {
  Kallik = 'Kallik',
  Esko = 'Esko',
  Veeva = 'Veeva',
  VerifyAPI = 'VerifyAPI',
}

const integrationDestinationsMap: Record<IntegrationNames, ReportDestinations> = {
  [IntegrationNames.Esko]: ReportDestinations.CompleteWithEsko,
  [IntegrationNames.Kallik]: ReportDestinations.FinishWithVeraciti,
  [IntegrationNames.Veeva]: ReportDestinations.VeevaVault,
  [IntegrationNames.VerifyAPI]: ReportDestinations.CompleteWithVerifyAPI,
};

const useStyles = makeStyles()((theme: Theme) => {
  return {
    reportText: {
      display: 'none',
      '@container gv-toolbar (min-width: 1500px)': {
        display: 'inline',
      },
    },
    downloadButton: {
      border: '1px solid rgb(16, 17, 19) !important',
      backgroundColor: menuLightGrey,
      padding: '7px 10px 8px 10px',
      height: '32px',
      width: '32px',
      minWidth: '32px',
      gap: '0',
      '@container gv-toolbar (min-width: 1500px)': {
        width: '75px',
        minWidth: '75px',
        gap: '6px',
      },
    },
    downloadButtonReviewRequired: {
      width: '46px',
      minWidth: '46px',
      '@container gv-toolbar (min-width: 1500px)': {
        width: '98px',
        minWidth: '98px',
      },
    },
    dialogContent: {
      width: '516px',
      maxWidth: '100%',
      padding: '24px 24px 32px 24px',
    },
    dialogButton: {
      backgroundColor: menuLightGrey,
      '&.Mui-disabled': {
        color: 'rgba(238, 233, 233, 0.77)',
      },
    },
    fullWidth: {
      width: '100%',
    },
    alert: {
      alignItems: 'center',
      display: 'flex',
      fontSize: '12px',
      fontWeight: 600,
      paddingRight: '20px',
      color: '#1A425F',
      '& .MuiAlert-icon': {
        fontSize: 20,
        color: '#1A425F',
      },
    },
  };
});

type DownloadReportOptions = {
  annotatedSourceFile: boolean;
  annotatedNewFile: boolean;
  summaryReport: boolean;
  detailedReport: boolean;
  includeLinkedInspections: boolean;
  uploadGroundTruth: boolean;
};

export interface MultiInspectionMode {
  open: boolean;
  onClose: () => void;
  inspectionIds: string[];
  onGenerate: (payload: CreateMultiInspectionReportPayload, reportName: string) => void;
  anyHaveSourceFile: boolean;
}

interface ReportDownloadMenuProps {
  // Simplified multi-inspection dialog.
  multiInspection?: MultiInspectionMode;
}

const ReportDownloadMenu = ({ multiInspection }: ReportDownloadMenuProps = {}) => {
  const isMultiMode = Boolean(multiInspection);
  const dispatch = useDispatch();
  const { classes, cx } = useStyles();
  const { user } = useUserStateStore();
  const orgId = getOrganizationId(user);
  const { data: orgData } = useOrganizationQuery(orgId, {
    includeUsers: false,
  });
  const [reportOptions, setReportOptions] = useState<DownloadReportOptions>({
    annotatedNewFile: false,
    annotatedSourceFile: false,
    summaryReport: false,
    detailedReport: false,
    includeLinkedInspections: true,
    uploadGroundTruth: false,
  });
  const [selectedLinkedInspectionIds, setSelectedLinkedInspectionIds] = useState<string[]>([]);
  const isManualToggle = useRef(false); // Track if user is manually toggling the main checkbox
  // Container to portal overlays (tooltips, Destination Select) inside the dialog.
  // MUI Dialog marks body siblings as aria-hidden; a body-level Select portal would
  // be inaccessible to getByRole. Stored in state (not a ref) so Portals re-render
  // with the resolved node — refs do not trigger re-renders.
  const [tooltipContainer, setTooltipContainer] = useState<HTMLDivElement | null>(null);
  const isProofreaderMode = useSelector(getIsSingleFile);
  const inspectionId = useSelector(getInspectionId);
  const hasSetDefaultForInspection = useRef<string | null>(null); // Track which inspection we've set default for
  const lastJobId = useSelector(getInspectionLastJobId);
  const focusedDifferenceId = useSelector(getFocusedDifferenceId);
  const internalAnnotationVisibility = useSelector(getInternalAnnotationVisibility);
  const differences = useSelector(getDifferences);
  const { fileName: targetFileName } = useSelector(getTargetFile);
  const showTextHighlights = useSelector(getShowTextHighlights);
  const inspectionSettings = useSelector(getInspectionSettings);
  const tenantId = getTenantId(user as User);
  const { data: orgIntegrations } = useQuery({ type: fetchOrgIntegrations });
  const inspectionQuery = useQuery({ type: fetchInspection });
  const {
    reportPath,
    reportState,
    setReportState,
    reportDestination,
    setReportDestination,
    lastReportId,
    isMultiInspection,
  } = useReportState();
  const { data: statuses } = useInspectionCustomStatuses(inspectionId);
  const tracker = useTracker();
  const flags = useFlags();

  const groupId = inspectionSettings?.groupId;
  const { data: linkedInspections } = useQuery<ApiLinkedInspection[]>({
    type: fetchLinkedInspections.toString(),
    requestKey: groupId ? `fetchLinkedInspections-${groupId}` : undefined,
  });

  // Fetch linked inspections when modal opens to get fresh data
  useEffect(() => {
    if (isMultiMode) return;
    if (reportState === ReportStates.Idle && groupId) {
      dispatch(fetchLinkedInspections(groupId));
    }
  }, [reportState, groupId, dispatch, isMultiMode]);

  // Get linked inspections with results
  const linkedInspectionsWithResults = useMemo(() => {
    if (!linkedInspections || !groupId) return [];
    return linkedInspections.filter((insp) => insp.lastJobId);
  }, [linkedInspections, groupId]);

  // Count linked inspections with results (have lastJobId regardless of status)
  const completedLinkedInspectionsCount = linkedInspectionsWithResults.length;

  // When force review is enabled, unreviewed linked inspections cannot be included in the generated report.
  const forceReviewEnabled = Boolean(orgData?.forceReview);
  const selectableLinkedInspectionIds = useMemo(
    () => linkedInspectionsWithResults.filter((insp) => !forceReviewEnabled || insp.reviewed).map((insp) => insp.id),
    [linkedInspectionsWithResults, forceReviewEnabled],
  );

  // Initialize selected linked inspections when modal opens or linked inspections change
  useEffect(() => {
    if (isMultiMode) return;
    if (reportState === ReportStates.Idle && linkedInspectionsWithResults.length > 0) {
      // Select all by default (excluding unreviewed ones when force review is on)
      setSelectedLinkedInspectionIds(selectableLinkedInspectionIds);
    }
  }, [reportState, linkedInspectionsWithResults, selectableLinkedInspectionIds, isMultiMode]);

  // Auto-toggle "Include linked inspections" based on selection (only when not manually toggling)
  useEffect(() => {
    if (isMultiMode) return;
    if (groupId && linkedInspectionsWithResults.length > 0 && !isManualToggle.current) {
      if (selectedLinkedInspectionIds.length === 0 && reportOptions.includeLinkedInspections) {
        // All deselected - uncheck the main checkbox
        setReportOptions((prev) => ({ ...prev, includeLinkedInspections: false }));
      } else if (selectedLinkedInspectionIds.length > 0 && !reportOptions.includeLinkedInspections) {
        // At least one selected - check the main checkbox
        setReportOptions((prev) => ({ ...prev, includeLinkedInspections: true }));
      }
    }
  }, [
    selectedLinkedInspectionIds.length,
    groupId,
    linkedInspectionsWithResults.length,
    reportOptions.includeLinkedInspections,
    isMultiMode,
  ]);

  // Add VerifyAPI integration for imported inspections (even if org doesn't have orgSettings.api)
  const integrations = useMemo(() => {
    const baseIntegrations = (orgIntegrations || []).filter((integration: any) => {
      // Skip invalid integrations
      if (!integration || !integration.displayName) {
        return false;
      }
      return true;
    });

    // Add VerifyAPI for imported inspections if not already present
    if (inspectionSettings?.isImported) {
      const hasVerifyAPI = baseIntegrations.some((int: any) => int.displayName === IntegrationNames.VerifyAPI);
      if (!hasVerifyAPI) {
        baseIntegrations.push({
          displayName: IntegrationNames.VerifyAPI,
          url: '',
          redirect: false,
        });
      }
    }

    return baseIntegrations;
  }, [orgIntegrations, inspectionSettings?.isImported]);
  const [reportName, setReportName] = useState<string>('');
  const isProcessing = reportState === 'generating' || reportState === 'downloading';
  const hasReportableJob = Boolean(
    inspectionQuery.data?.lastJob?.ctfJobs?.some(
      (job) => job.status === InspectionStatuses.completed && job.type !== CTFModuleTypes.textMatch,
    ),
  );

  const reviewAllRequired = useMemo(() => {
    if (!orgData?.forceReview) {
      return false;
    }

    // Check if all differences have been reviewed (have status, comment, or are false positive)
    const allDifferencesReviewed = differences.every((diff) => {
      const hasStatus = Boolean(diff.statusId);
      const hasComment = Boolean(diff.comment);

      return hasStatus || hasComment;
    });

    return allDifferencesReviewed ? false : differences.some((diff) => !diff.viewed);
  }, [focusedDifferenceId, orgData?.forceReview, lastJobId, differences]);

  const getPlaceholderReportName = () => {
    return `Report - ${targetFileName.split('.').slice(0, -1).join('.')}`;
  };

  const requiresNewReportOnly = useMemo(() => {
    switch (reportDestination) {
      case ReportDestinations.Download:
        return false;
      case ReportDestinations.VeevaVault:
        return 'Not Available for Veeva Integration.';
      case ReportDestinations.FinishWithVeraciti:
        return false; // Allow toggleable options for Veraciti
      case ReportDestinations.CompleteWithVerifyAPI:
        return false; // Allow toggleable options for VerifyAPI
      default:
        return 'Not Available.';
    }
  }, [reportDestination]);

  const shouldDisableOptions =
    reportDestination === ReportDestinations.CompleteWithEsko ||
    reportDestination === ReportDestinations.VeevaVault ||
    reportDestination === ReportDestinations.FinishWithVeraciti;

  useEffect(() => {
    if (isMultiMode) return;
    // By default, we set the report name with the new file name without the file extension.
    if (targetFileName && reportName === '') {
      const placeholderReportName = getPlaceholderReportName();
      setReportName(placeholderReportName);
    } else if (!targetFileName) {
      // when the target file is removed, we reset the reportname.
      setReportName('');
    }
  }, [targetFileName, isMultiMode]);

  // Auto-select destination based on whether inspection is imported (only once per inspection)
  useEffect(() => {
    if (isMultiMode) return;
    const hasVerifyAPI = integrations.some((int: any) => int.displayName === IntegrationNames.VerifyAPI);

    // Only set default once per inspection to allow user to change their mind
    if (hasSetDefaultForInspection.current !== inspectionId) {
      if (inspectionSettings?.isImported && hasVerifyAPI) {
        // Imported inspection: default to VerifyAPI
        setReportDestination(ReportDestinations.CompleteWithVerifyAPI);
      } else {
        // Non-imported inspection: default to Download
        setReportDestination(ReportDestinations.Download);
      }
      hasSetDefaultForInspection.current = inspectionId; // Mark as set for this inspection
    }
  }, [inspectionSettings?.isImported, integrations, inspectionId, setReportDestination, isMultiMode]);

  useEffect(() => {
    if (isMultiMode) return;
    // Initialize checkbox states when modal opens
    if (reportState === ReportStates.Idle) {
      // Initialize checkbox states based on current destination
      if (reportDestination === ReportDestinations.CompleteWithVerifyAPI) {
        // For VerifyAPI, select all report types (respecting single-file inspection limits)
        setReportOptions({
          annotatedNewFile: true,
          annotatedSourceFile: false,
          summaryReport: true,
          detailedReport: true,
          includeLinkedInspections: true,
          uploadGroundTruth: false,
        });
      } else if (reportDestination === ReportDestinations.FinishWithVeraciti) {
        // For Veraciti, only summary and detailed reports
        setReportOptions({
          annotatedNewFile: false,
          annotatedSourceFile: false,
          summaryReport: true,
          detailedReport: true,
          includeLinkedInspections: true,
          uploadGroundTruth: false,
        });
      } else if (reportDestination === ReportDestinations.CompleteWithEsko) {
        // For Esko, default to annotated new file and detailed thumbnail report
        setReportOptions({
          annotatedNewFile: true,
          annotatedSourceFile: false,
          summaryReport: false,
          detailedReport: true,
          includeLinkedInspections: true,
          uploadGroundTruth: false,
        });
      } else if (reportDestination === ReportDestinations.Download) {
        // For Download, pre-select Summary and Detailed so defaults are
        // deterministic instead of depending on whether `orgIntegrations`
        // is already cached when the modal mounts.
        setReportOptions({
          annotatedNewFile: false,
          annotatedSourceFile: false,
          summaryReport: true,
          detailedReport: true,
          includeLinkedInspections: true,
          uploadGroundTruth: false,
        });
      } else {
        // For other integrations (Veeva), default to annotated new file
        setReportOptions({
          annotatedNewFile: true,
          annotatedSourceFile: false,
          summaryReport: false,
          detailedReport: false,
          includeLinkedInspections: true,
          uploadGroundTruth: false,
        });
      }
    }
  }, [reportState, reportDestination, isProofreaderMode, setReportOptions, isMultiMode]);

  useEffect(() => {
    if (isMultiMode) return;
    if (user) {
      dispatch(fetchOrgIntegrations(getOrganizationId(user as User)));
    }
  }, [user, tenantId, isMultiMode]);

  useEffect(() => {
    if (isMultiMode) return;
    // If the organization has no integrations, default to summary and detailed enabled
    if (!orgIntegrations || (Array.isArray(orgIntegrations) && orgIntegrations.length === 0)) {
      setReportOptions((prev) => ({
        ...prev,
        summaryReport: true,
        detailedReport: true,
        includeLinkedInspections: true,
      }));
    }
  }, [orgIntegrations, isMultiMode]);

  useEffect(() => {
    if (isMultiMode) return;
    // handling the report action once the report has been generated.
    if (reportState === ReportStates.Generated) {
      if (reportDestination === ReportDestinations.Download) {
        tracker.track({
          name: 'report-downloaded',
          data: {
            reportName,
            downloadReportOptions: reportOptions,
          },
        });
        setReportState(ReportStates.Downloading);
        dispatch(
          report.actions.downloadReport({
            reportName,
            reportPath: reportPath,
          }),
        );
      } else if (reportDestination === ReportDestinations.CompleteWithEsko) {
        window.location.href = `${import.meta.env.VITE_ESKO_REDIRECT_URL}?id=${inspectionId}`;
      } else if (reportDestination === ReportDestinations.FinishWithVeraciti) {
        const int: any = integrations.find((integration: any) => integration.displayName === IntegrationNames.Kallik);

        // for kallik specifically, the structure looks different.
        // Auth0 integrations use 'redirectUrl' property
        if (int?.redirectUrl) {
          window.location.href = `${int.redirectUrl}?id=${inspectionId}`;
        }
      } else {
        // Find integration by matching the destination to the integration name
        // reportDestination is the mapped name (e.g., "Complete with Verify Compare API")
        // integration.displayName is the backend name (e.g., "VerifyAPI")
        const int: any = integrations.find((integration: any) => {
          const mappedDestination = integrationDestinationsMap[integration.displayName as IntegrationNames];
          return mappedDestination === reportDestination || integration.displayName === reportDestination;
        });

        if (!int) {
          console.error(`Could not find integration for destination: ${reportDestination}`);
          throw new Error(`Integration not found for destination: ${reportDestination}`);
        }

        const reportName = int.displayName;
        tracker.track({
          name: 'report-upload',
          data: {
            reportDestination,
            reportName,
          },
        });

        // DB integrations (Veeva) use 'redirect' and 'url' properties
        if (int.redirect) {
          // perform a redirect to callback URL passing email, inspectionId and reportId
          const urlParams = qs.stringify({
            email: user?.email,
            inspectionId,
            id: inspectionId,
            reportId: lastReportId,
          });
          window.open(`${int.url}?${urlParams}`, '_blank')?.focus();
        } else {
          dispatch(uploadReport(inspectionId, int.displayName, reportName, lastReportId, user?.email));
        }
        setReportState(ReportStates.Closed);
      }
    } else if (reportState === ReportStates.Closed) {
      dispatch(stopPolling([pollReportById.toString()]));
      dispatch(abortRequests([createReport.toString()]));
    }
  }, [reportState, isMultiMode]);

  useEffect(() => {
    // In multi-inspection mode the polling lifecycle is owned by the
    // generating page (SessionList): the modal closes immediately on submit
    // and the report keeps polling until it resolves. Stopping polling on
    // unmount here would cancel an in-flight multi report when the user
    // navigates away from Home, since multi reports reuse `pollReportById`.
    if (isMultiMode) return;
    return () => {
      // Single-mode cleanup must not abort an in-flight multi-inspection
      // report's polling: a single-mode menu mounts on every Results page
      // and unmounting it would otherwise cancel the multi report that is
      // still running in the background.
      if (useReportState.getState().isMultiInspection) return;
      dispatch(stopPolling([pollReportById.toString()]));
      dispatch(abortRequests([createReport.toString()]));
    };
  }, [isMultiMode]);

  // Multi-mode: reset report name and options each time the modal opens.
  useEffect(() => {
    if (!multiInspection?.open) return;
    setReportName('Multi-Inspection Report');
    setReportOptions({
      annotatedNewFile: false,
      annotatedSourceFile: false,
      summaryReport: true,
      detailedReport: true,
      includeLinkedInspections: false,
      uploadGroundTruth: false,
    });
  }, [multiInspection?.open]);

  // Multi-mode: clear "Annotated Source File" when no picked inspection has a source.
  // Depends only on the boolean so the effect doesn't re-fire on every parent
  // render that recreates the `multiInspection` prop reference.
  useEffect(() => {
    if (!multiInspection || multiInspection.anyHaveSourceFile) return;
    setReportOptions((prev) => (prev.annotatedSourceFile ? { ...prev, annotatedSourceFile: false } : prev));
  }, [multiInspection?.anyHaveSourceFile]);

  const handleMultiGenerate = () => {
    if (!multiInspection) return;
    const showTextHighlightsDefault = true;
    const payload: CreateMultiInspectionReportPayload = {
      inspectionIds: multiInspection.inspectionIds,
      ctfVersion: undefined,
    };
    if (reportOptions.annotatedNewFile || reportOptions.annotatedSourceFile) {
      payload.annotatedReport = {
        source: reportOptions.annotatedSourceFile,
        new: reportOptions.annotatedNewFile,
        keepExistingAnnotations: false,
        showTextHighlights: showTextHighlightsDefault,
      };
    }
    if (reportOptions.summaryReport) {
      payload.summaryReport = { showTextHighlights: showTextHighlightsDefault };
    }
    if (reportOptions.detailedReport) {
      payload.detailedReport = { includeThumbnails: true, showTextHighlights: showTextHighlightsDefault };
    }
    multiInspection.onGenerate(payload, reportName);
    multiInspection.onClose();
  };

  const handleReportAction = () => {
    setReportState(ReportStates.Generating);
    let createReportPayload: CreateReportPayload = {};

    // if either annotated source file or annotated new file is selected, we ask to create an annotated report.
    if (reportOptions.annotatedSourceFile === true || reportOptions.annotatedNewFile === true) {
      createReportPayload['annotatedReport'] = {
        source: reportOptions.annotatedSourceFile,
        new: reportOptions.annotatedNewFile,
        keepExistingAnnotations: internalAnnotationVisibility,
        showTextHighlights,
      };
    }

    // If either Summary or Detailed is selected, compute excluded results (used by both)
    if (reportOptions.summaryReport === true || reportOptions.detailedReport === true) {
      // we send a formatted string to ctf to summarize excluded differences from the report.
      // e.g., "font color (4), line breaks (3), smart discards (5), Unassigned (13)"
      // 1. get all differences and filters part of the report
      const state = store.getState();
      const differences = getDifferences(state);
      const typeFilters = getDifferenceTypeFilters(state);
      const statusFilters = getDifferenceStatusFilters(state);

      // 2. get counts of filtered or smart discarded differences
      const excludedCounts: Record<string, number> = { smartDiscarded: 0, unassigned: 0 };

      Object.keys(typeFilters).forEach((filter) => {
        excludedCounts[filter] = 0;
      });

      if (statuses) {
        for (const statusId in statuses.allStatuses) {
          const status = statuses.allStatuses[statusId];
          excludedCounts[status.title] = 0;
        }
      }

      differences.forEach((diff) => {
        if (diff.smartDiscard && statusFilters.smartDiscard === true) {
          excludedCounts.smartDiscarded++;
        }

        if (statusFilters?.unassigned && !diff.statusId) {
          excludedCounts.unassigned++;
        }

        if (typeFilters[diff.type] === true) {
          // Apply filter type mappings first (Hyphen → Change, Superscript → Subscript, Size → FontSize)
          const mappedType = filterTypeMappings[diff.type] || diff.type;
          const displayName = getDifferenceDisplayName(mappedType as DifferenceTypeNames);
          excludedCounts[displayName] = (excludedCounts[displayName] || 0) + 1;
        }

        if (diff.statusId && statusFilters[diff.statusId] === true && statuses) {
          const status = statuses.allStatuses[diff.statusId];
          excludedCounts[status.title]++;
        }
      });

      // 3. with the counts, create a formatted string to be sent to ctf.
      const excludedResults = Object.entries(excludedCounts).filter(([_, count]) => count > 0);
      const getExcludedLabel = (key: string) => {
        if (key === 'smartDiscarded') return 'Smart Discards';
        // Key is already the display name, so return it directly
        return key;
      };
      const excludedResultsString = excludedResults
        .map(([key, count]) => `${getExcludedLabel(key)}: ${count}`)
        .join(', ');
      if (reportOptions.summaryReport) {
        createReportPayload['summaryReport'] = {
          excludedResults: excludedResultsString,
          showTextHighlights,
        };
      }
      if (reportOptions.detailedReport) {
        createReportPayload['detailedReport'] = {
          includeThumbnails: true,
          excludedResults: excludedResultsString,
          showTextHighlights,
        };
      }
    }

    if (buttonLabel() === 'Complete Report') {
      createReportPayload['xfdfReport'] = true;
    }

    if (reportOptions.uploadGroundTruth) {
      createReportPayload['uploadGroundTruth'] = true;
    }

    // Add linked inspections flag or specific IDs
    if (groupId && reportOptions.includeLinkedInspections) {
      // Check if all linked inspections are selected
      const allSelected = selectedLinkedInspectionIds.length === linkedInspectionsWithResults.length;

      if (allSelected) {
        // Include all - use the flag
        createReportPayload['includeLinkedInspections'] = true;
      } else if (selectedLinkedInspectionIds.length > 0) {
        // User selected specific ones - use the IDs
        createReportPayload['linkedInspectionIds'] = selectedLinkedInspectionIds;
      }
    }

    dispatch(createReport(inspectionId, createReportPayload));
  };

  const handleCloseModal = () => {
    dispatch(stopPolling([pollReportById.toString()]));
    dispatch(abortRequests([createReport.toString()]));
    setReportState(ReportStates.Closed);
  };

  const handleFileNameChange = (newName: string) => {
    setReportName(newName);
  };

  const handleDestinationChange = (newDestination: ReportDestinations) => {
    if (newDestination === ReportDestinations.CompleteWithVerifyAPI) {
      // For VerifyAPI, select all report types (user can change)
      // Note: annotatedSourceFile only if not single-file inspection
      setReportOptions({
        annotatedNewFile: true,
        annotatedSourceFile: false,
        summaryReport: true,
        detailedReport: true,
        includeLinkedInspections: true,
        uploadGroundTruth: false,
      });
    } else if (newDestination === ReportDestinations.FinishWithVeraciti) {
      // For veraciti, they have asked to limit reports; preserve existing default selections
      setReportOptions({
        annotatedNewFile: false,
        annotatedSourceFile: false,
        summaryReport: true,
        detailedReport: true,
        includeLinkedInspections: true,
        uploadGroundTruth: false,
      });
    } else if (newDestination === ReportDestinations.CompleteWithEsko) {
      setReportOptions({
        annotatedNewFile: true,
        annotatedSourceFile: false,
        summaryReport: false,
        detailedReport: true,
        includeLinkedInspections: true,
        uploadGroundTruth: false,
      }); // Esko expects annotated new and detailed thumbnail report.
    } else if (newDestination !== ReportDestinations.Download) {
      setReportOptions({
        annotatedNewFile: true,
        annotatedSourceFile: false,
        summaryReport: false,
        detailedReport: false,
        includeLinkedInspections: true,
        uploadGroundTruth: false,
      }); // Integration options expects only annotated new for non-Veraciti integrations.
    }

    if (newDestination === ReportDestinations.FinishWithVeraciti) {
      // kallik requested disabling editing report name for their own purposes. (VE-9565)
      const placeholderReportName = getPlaceholderReportName();
      setReportName(placeholderReportName);
    }
    setReportDestination(newDestination);
  };

  const toggleCheckbox = (
    type:
      | 'annotatedNewFile'
      | 'annotatedSourceFile'
      | 'summaryReport'
      | 'detailedReport'
      | 'includeLinkedInspections'
      | 'uploadGroundTruth',
  ) => {
    const newReportOptions = { ...reportOptions, [type]: !reportOptions[type] };
    setReportOptions(newReportOptions);
  };

  const integrationDestinations = integrations.map(({ displayName }) => {
    return integrationDestinationsMap[displayName as IntegrationNames] || displayName;
  });

  const destinationsOptions: ReportDestinations[] = [ReportDestinations.Download, ...integrationDestinations];
  const destinationSelectOptions = destinationsOptions.map((option) => ({
    value: option,
    label: option,
  }));

  const buttonLabel = () => {
    if (isProcessing) {
      return (
        <>
          <CircularProgress size={17} color="inherit" sx={{ marginRight: 1 }} />
          Generating Report
        </>
      );
    }

    if (reportDestination === ReportDestinations.Download) {
      return 'Download Report';
    } else if (reportDestination === ReportDestinations.VeevaVault) {
      return 'Upload Report';
    }
    return 'Complete Report';
  };

  const dialogOpen = multiInspection ? multiInspection.open : !isMultiInspection && reportState !== ReportStates.Closed;
  const handleClose = multiInspection ? multiInspection.onClose : handleCloseModal;
  const subheadingText = multiInspection
    ? `Download your combined report from ${multiInspection.inspectionIds.length} selected inspection${
        multiInspection.inspectionIds.length > 1 ? 's' : ''
      }.`
    : 'Download your report here. Filters and sorting applied to the difference list will be reflected in the report.';

  return (
    <>
      {!isMultiMode && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <GVTooltip
            title={
              reviewAllRequired
                ? 'Reports are available for download once all inspection results have been reviewed.'
                : !hasReportableJob
                  ? 'No successful inspection results to include in a report.'
                  : 'Generate Report'
            }
            side={reviewAllRequired || !hasReportableJob ? 'bottom' : 'top'}
            className={styles.buttonTooltip}
          >
            <Grid>
              <Button
                variant="outlined"
                color="primary"
                id="downloadButton"
                onClick={() => setReportState(ReportStates.Idle)}
                className={cx(
                  classes.downloadButton,
                  (reviewAllRequired || !hasReportableJob) && classes.downloadButtonReviewRequired,
                )}
                disabled={reviewAllRequired || !hasReportableJob}
              >
                <FileDownloadOutlinedIcon color="primary" fontSize="small" />
                {reviewAllRequired && (
                  <WarningAmberIcon
                    sx={{ height: '16px', width: '16px', marginLeft: '2px', color: colors.gvYellow, opacity: 1 }}
                  />
                )}
                <span className={classes.reportText}>Report</span>
              </Button>
            </Grid>
          </GVTooltip>
        </Box>
      )}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        className={styles.dialog}
        style={isProcessing ? { zIndex: REPORT_DOWNLOAD_DIALOG_Z_INDEX } : undefined}
      >
        <Grid
          data-testid={isMultiMode ? 'merged-report-modal' : undefined}
          sx={{ background: 'var(--gds-color-cool-gray-700)' }}
        >
          <Grid
            item
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '-40px' }}
          >
            <DialogTitle sx={{ paddingX: '24px', fontWeight: 600 }}>Download report</DialogTitle>
            <GVIconButton
              size="small"
              onClick={handleClose}
              icon={<CloseIcon color="primary" />}
              sx={{ marginRight: '24px' }}
            />
          </Grid>
          <DialogContent className={classes.dialogContent} ref={setTooltipContainer}>
            <Grid container className={classes.fullWidth} spacing={1.25}>
              {!isMultiMode && isProcessing && (
                <Grid item className={classes.fullWidth}>
                  <Alert className={classes.alert} severity="info" sx={{ paddingY: '2px', marginTop: '16px' }}>
                    Your report is being generated. The dialog will close automatically once it's finished.
                  </Alert>
                </Grid>
              )}
              <Grid item className={classes.fullWidth}>
                <Grid>
                  <GVTypography sx={{ paddingY: '4px', marginTop: '8px', marginBottom: '12px' }}>
                    {subheadingText}
                  </GVTypography>
                </Grid>
                <Grid item sx={{ padding: '16px 8px', marginBottom: '16px' }}>
                  <GVTextField
                    placeholder="Type report name..."
                    isDisabled={!isMultiMode && (isProcessing || shouldDisableOptions)}
                    id="reportName"
                    onValueChange={(value) => {
                      handleFileNameChange(value ?? '');
                    }}
                    label="File Name"
                    value={reportName}
                  />
                  {!isMultiMode && (
                    <Grid item>
                      <GVSelect
                        isDisabled={isProcessing}
                        options={destinationSelectOptions}
                        label="Destination"
                        placeholder="Select a destination..."
                        onValueChange={(nextValue) => handleDestinationChange(nextValue as ReportDestinations)}
                        value={reportDestination}
                        container={tooltipContainer}
                      />
                    </Grid>
                  )}
                  <Grid item container direction="column" sx={{ marginLeft: '8px' }}>
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                      <GVCheckbox
                        label="Inspection Summary Page"
                        data-testid="inspection-summary-page"
                        checked={reportOptions.summaryReport}
                        onChange={() => toggleCheckbox('summaryReport')}
                        disabled={!isMultiMode && (isProcessing || shouldDisableOptions)}
                      />
                      {!isMultiMode && (
                        <GVTooltip
                          container={tooltipContainer}
                          side="top"
                          title={
                            <GVTypography variant="inherit">
                              {requiresNewReportOnly
                                ? requiresNewReportOnly
                                : 'This summary contains all of the relevant inspection details in one page, including inspection parameters from Prep, as well as filtered exclusion information from Results.'}
                            </GVTypography>
                          }
                          className={styles.tooltip}
                        >
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <InfoOutlinedIcon sx={{ height: '16px', marginLeft: '-8px' }} />
                          </span>
                        </GVTooltip>
                      )}
                    </Grid>
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                      <GVCheckbox
                        label="Detailed Thumbnail Report"
                        data-testid="detailed-thumbnail-report"
                        checked={reportOptions.detailedReport}
                        onChange={() => toggleCheckbox('detailedReport')}
                        disabled={!isMultiMode && (isProcessing || shouldDisableOptions)}
                      />
                    </Grid>
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                      <GVCheckbox
                        label="Annotated Source File"
                        data-testid="annotated-source-file"
                        checked={reportOptions.annotatedSourceFile}
                        onChange={() => toggleCheckbox('annotatedSourceFile')}
                        disabled={
                          isMultiMode
                            ? !multiInspection?.anyHaveSourceFile
                            : isProofreaderMode || isProcessing || shouldDisableOptions
                        }
                      />
                      {isMultiMode
                        ? !multiInspection?.anyHaveSourceFile && (
                            <GVTooltip
                              container={tooltipContainer}
                              title={
                                <GVTypography variant="inherit">
                                  Not available when no selected inspections have a source file.
                                </GVTypography>
                              }
                              className={styles.tooltip}
                            >
                              <span style={{ display: 'flex', alignItems: 'center' }}>
                                <InfoOutlinedIcon sx={{ height: '16px', marginLeft: '-8px' }} />
                              </span>
                            </GVTooltip>
                          )
                        : (isProofreaderMode || requiresNewReportOnly) && (
                            <GVTooltip
                              side="top"
                              container={tooltipContainer}
                              title={
                                <GVTypography variant="inherit">
                                  {requiresNewReportOnly
                                    ? requiresNewReportOnly
                                    : 'Not Available for single-file inspections.'}
                                </GVTypography>
                              }
                              className={styles.tooltip}
                            >
                              <span style={{ display: 'flex', alignItems: 'center' }}>
                                <InfoOutlinedIcon sx={{ height: '16px', marginLeft: '-8px' }} />
                              </span>
                            </GVTooltip>
                          )}
                    </Grid>
                    <GVCheckbox
                      label={'Annotated New File'}
                      data-testid="annotated-new-file"
                      checked={reportOptions.annotatedNewFile}
                      onChange={() => toggleCheckbox('annotatedNewFile')}
                      disabled={!isMultiMode && (isProcessing || shouldDisableOptions)}
                    />
                    {!isMultiMode && flags.uploadGroundTruthCheckbox && (
                      <GVCheckbox
                        label="Upload ground truth"
                        checked={reportOptions.uploadGroundTruth}
                        onChange={() => toggleCheckbox('uploadGroundTruth')}
                        disabled={isProcessing || shouldDisableOptions}
                      />
                    )}
                    {!isMultiMode && groupId && completedLinkedInspectionsCount > 0 && (
                      <>
                        <Grid item sx={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                          <GVCheckbox
                            label={`Include linked inspections (${completedLinkedInspectionsCount} with results)`}
                            checked={reportOptions.includeLinkedInspections}
                            data-testid="include-linked-inspections"
                            onChange={() => {
                              isManualToggle.current = true; // Set flag to prevent auto-toggle
                              const newValue = !reportOptions.includeLinkedInspections;
                              toggleCheckbox('includeLinkedInspections');
                              // If checking it back on, select all inspections
                              // (excluding unreviewed ones when force review is on)
                              if (newValue) {
                                setSelectedLinkedInspectionIds(selectableLinkedInspectionIds);
                              }
                              // Reset flag after a short delay to allow auto-toggle on individual selection
                              setTimeout(() => {
                                isManualToggle.current = false;
                              }, 100);
                            }}
                            disabled={isProcessing || shouldDisableOptions}
                          />
                          <GVTooltip
                            container={tooltipContainer}
                            title="Generate a combined report with linked inspections that have results."
                            id="linked-inspections-tooltip"
                            className={styles.tooltip}
                          >
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <InfoOutlinedIcon sx={{ height: '16px', marginLeft: '-8px' }} />
                            </span>
                          </GVTooltip>
                        </Grid>

                        {/* Individual linked inspection selection */}
                        {reportOptions.includeLinkedInspections && linkedInspectionsWithResults.length > 0 && (
                          <Box sx={{ marginLeft: '24px', marginTop: '8px', marginBottom: '8px' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                              }}
                            >
                              <GVTypography variant="caption" color="textSecondary">
                                Select inspections to include:
                              </GVTypography>
                              <Button
                                size="small"
                                data-testid="select-all-linked-inspections"
                                onClick={() => {
                                  if (selectedLinkedInspectionIds.length === selectableLinkedInspectionIds.length) {
                                    setSelectedLinkedInspectionIds([]);
                                  } else {
                                    setSelectedLinkedInspectionIds(selectableLinkedInspectionIds);
                                  }
                                }}
                                disabled={isProcessing || shouldDisableOptions}
                                sx={{ fontSize: '11px', minWidth: 'auto', padding: '2px 8px' }}
                              >
                                {selectedLinkedInspectionIds.length === selectableLinkedInspectionIds.length
                                  ? 'Deselect All'
                                  : 'Select All'}
                              </Button>
                            </Box>
                            <Box
                              sx={{
                                maxHeight: '150px',
                                overflowY: 'auto',
                                paddingRight: '8px',
                                paddingLeft: '16px',
                              }}
                            >
                              {linkedInspectionsWithResults.map((linkedInsp) => {
                                const isReviewBlocked = forceReviewEnabled && !linkedInsp.reviewed;
                                const checkbox = (
                                  <GVCheckbox
                                    label={
                                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <GVTypography variant="body2" noWrap sx={{ maxWidth: '300px' }}>
                                          {linkedInsp.name}
                                          {linkedInsp.id === inspectionId && (
                                            <GVTypography
                                              component="span"
                                              variant="caption"
                                              color="textSecondary"
                                              sx={{ marginLeft: '4px' }}
                                            >
                                              (Current)
                                            </GVTypography>
                                          )}
                                        </GVTypography>
                                      </Box>
                                    }
                                    id={`linked-inspection-${linkedInsp.name}`}
                                    checked={selectedLinkedInspectionIds.includes(linkedInsp.id)}
                                    onChange={() => {
                                      setSelectedLinkedInspectionIds((prev) =>
                                        prev.includes(linkedInsp.id)
                                          ? prev.filter((id) => id !== linkedInsp.id)
                                          : [...prev, linkedInsp.id],
                                      );
                                    }}
                                    disabled={isProcessing || shouldDisableOptions || isReviewBlocked}
                                    disableRipple
                                  />
                                );
                                return (
                                  <Box
                                    key={linkedInsp.id}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      marginBottom: '4px',
                                      '& .MuiButtonBase-root:hover': {
                                        backgroundColor: 'transparent',
                                      },
                                    }}
                                  >
                                    {isReviewBlocked ? (
                                      <GVTooltip
                                        title="This linked inspection has not been reviewed. Review all differences before including it in the report."
                                        container={tooltipContainer}
                                        className={styles.tooltip}
                                      >
                                        <span style={{ display: 'flex' }}>{checkbox}</span>
                                      </GVTooltip>
                                    ) : (
                                      checkbox
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <DialogActions>
              <Button
                color="inherit"
                onClick={handleClose}
                data-testid={isMultiMode ? 'cancel-merged-report' : undefined}
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                variant="contained"
                data-testid={isMultiMode ? 'download-merged-report' : undefined}
                disabled={
                  isMultiMode
                    ? reportName === '' ||
                      (!reportOptions.annotatedNewFile &&
                        !reportOptions.annotatedSourceFile &&
                        !reportOptions.summaryReport &&
                        !reportOptions.detailedReport)
                    : reviewAllRequired ||
                      isProcessing ||
                      reportName === '' ||
                      // Check if any actual report type is selected (excluding includeLinkedInspections)
                      (!reportOptions.annotatedNewFile &&
                        !reportOptions.annotatedSourceFile &&
                        !reportOptions.summaryReport &&
                        !reportOptions.detailedReport) ||
                      // Only validate linked inspections if inspection is actually part of a group
                      (Boolean(groupId) &&
                        reportOptions.includeLinkedInspections &&
                        selectedLinkedInspectionIds.length === 0)
                }
                onClick={isMultiMode ? handleMultiGenerate : handleReportAction}
              >
                {isMultiMode ? 'Download Report' : buttonLabel()}
              </Button>
            </DialogActions>
          </DialogContent>
        </Grid>
      </Dialog>
    </>
  );
};

export default ReportDownloadMenu;
